import { Bell, X } from 'lucide-react'
import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { useFocusRequest } from '../platform/a11y/useFocusRequest'
import { emit } from '../platform/analytics/analytics'
import { Async } from '../platform/async/Async'
import { Skeleton } from '../platform/async/Skeleton'
import { useToast } from '../platform/feedback/ToastProvider'
import { Icon } from '../platform/icons/Icon'
import { Overlay } from '../platform/overlay/Overlay'
import { Button } from '../platform/primitives/Button'
import { useHitTarget } from '../platform/responsive/hitTarget'
import {
  useMarkAllRead,
  useMarkRead,
  useNotifications,
  useRefreshNotifications,
  useUnreadCount,
  type Notification,
  type NotificationList,
} from './useNotifications'
import './notifications.css'

/**
 * M0 0.13's notifications drawer, and the bell that opens it.
 *
 * One component rather than two files: the trigger owns the open state and the
 * ref focus returns to, and splitting them would mean lifting both into
 * `TopBar`, which has no other reason to know this feature exists.
 *
 * **Tap-through to the related entity is deferred to Phase 3 part 2.**
 * `NotificationResponse` carries `type` and a free-form `data` object, and the
 * notification-to-destination mapping is a real design decision with no owner
 * until M5/M6/M7 supply the destinations. A row that looks tappable and lands
 * nowhere is worse than one that does not claim to be. So rows are not
 * controls; the only per-row action is marking it read.
 */

/**
 * **Alt+N (Option+N), and #110's ⌘K will have to coexist with it.**
 *
 * ⌘K is the palette's. A bare letter — the `n` that GitHub or Linear would use
 * — fails WCAG 2.1.4, which requires a single-character shortcut to be
 * remappable or off, and there is no settings surface to remap it in. Every
 * ⌘⇧ pair worth having is claimed by a browser: ⌘⇧A is Chrome's tab search,
 * ⌘⇧N is incognito, ⌘⇧J and ⌘⇧I are devtools. Alt+letter is free in all
 * three engines.
 *
 * Matched on `event.code`, not `event.key`: Option+N on a US Mac layout is a
 * dead key and reports `key === 'Dead'`, so a `key === 'n'` test would never
 * fire on the platform the design targets first.
 *
 * When the palette lands there will be two global bindings and no registry to
 * hold them. That is the point at which one should exist — recorded here
 * rather than built now for a single caller.
 */
const SHORTCUT = { code: 'KeyN', alt: true } as const

/** A global key listener reaches a focused field. Nothing here should fire
 *  while the user is typing, even behind a modifier. */
function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  return (
    target.isContentEditable ||
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement
  )
}

const DAY_LABEL = new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
const TIME_LABEL = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit' })

/** Midnight-relative, not 24-hours-relative: "Yesterday" means the calendar
 *  day before this one, which is what a reader means by it. */
function dayLabel(when: Date, now: Date): string {
  const days = Math.round(
    (new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime() -
      new Date(when.getFullYear(), when.getMonth(), when.getDate()).getTime()) /
      86_400_000,
  )
  if (days <= 0) return 'Today'
  if (days === 1) return 'Yesterday'
  return DAY_LABEL.format(when)
}

type Group = { label: string; items: Notification[] }

/**
 * Grouped by time, as spec §0.13 requires. Order is preserved rather than
 * sorted: the server returns newest-first and re-sorting here would be a
 * second opinion about ordering that the two clients could disagree on.
 */
export function groupByDay(items: readonly Notification[], now: Date): Group[] {
  const groups: Group[] = []
  for (const item of items) {
    const label = dayLabel(new Date(item.created_at), now)
    const last = groups.at(-1)
    if (last?.label === label) last.items.push(item)
    else groups.push({ label, items: [item] })
  }
  return groups
}

/**
 * **These in-drawer controls do NOT meet the 44px coarse target, and that is
 * #90, not an oversight here.** `Button` writes `min-height` inline from a
 * viewport-invariant map, placed AFTER the caller's `style` precisely so a
 * caller cannot change it — so passing one from here is silently ignored, and
 * a `style` prop that looks like it raises the floor would be worse than the
 * gap. The fix is the one line in `Button` that #90 owns, and it moves every
 * button in the app. The bell below, which this issue actually specifies, is a
 * raw control and does meet it.
 */
function Row({ item, onRead }: { item: Notification; onRead: (id: Notification['id']) => void }) {
  return (
    <li className="notif__item">
      <div className="notif__item-head">
        <span className="notif__item-title">{item.title}</span>
        <span className="notif__time">{TIME_LABEL.format(new Date(item.created_at))}</span>
      </div>
      <p className="notif__item-message">{item.message}</p>
      {/* The unread signal is the CONTROL, not the dot. Colour is never the
          only signal, and a read row simply has nothing to act on. */}
      {!item.is_read && (
        <span className="notif__item-actions">
          <span aria-hidden="true" className="notif__dot" />
          <Button size="page" variant="outline" onClick={() => onRead(item.id)}>
            Mark as read
          </Button>
        </span>
      )}
    </li>
  )
}

function Body({ list, onRead }: { list: NotificationList; onRead: (id: Notification['id']) => void }) {
  const now = new Date()
  const base = useId()
  return (
    <div className="notif__groups">
      {groupByDay(list.notifications, now).map((group, index) => {
        /**
         * Named ONCE, and by `aria-label` rather than by the visible node.
         *
         * Three shapes were tried and the reasons matter, because the obvious
         * two are both wrong:
         *
         *  - `aria-label` + a visible `<h3>` announced the label twice, and put
         *    an h3 under the breadcrumb's h1 with no h2 between them.
         *  - `aria-labelledby` pointing at the visible node fixed the double
         *    announcement and broke the CASING: an accessible name computed
         *    from rendered text inherits `text-transform: uppercase`, so
         *    Chrome's accessibility tree read `["TODAY","YESTERDAY","01 AUG
         *    2026"]`. An `aria-label` does not go through rendered text, so it
         *    reads `["Today","Yesterday","01 Aug 2026"]`. jsdom does not apply
         *    text-transform when computing a name, so `toHaveAccessibleName`
         *    was green either way — this was measured over CDP.
         *
         * So: `aria-label` carries the name, and the visible node is hidden
         * from assistive technology because it would otherwise be read a second
         * time as content. One announcement, correct casing, no heading.
         */
        const labelId = `${base}-${index}`
        return (
          <section key={group.label} role="group" aria-label={group.label} className="notif__group">
            <span id={labelId} aria-hidden="true" className="notif__group-label">
              {group.label}
            </span>
            <ul className="notif__list">
              {group.items.map((item) => (
                <Row key={item.id} item={item} onRead={onRead} />
              ))}
            </ul>
          </section>
        )
      })}
    </div>
  )
}

export function NotificationsBell() {
  const [open, setOpen] = useState(false)
  const trigger = useRef<HTMLButtonElement>(null)
  const title = useRef<HTMLSpanElement>(null)
  const unread = useUnreadCount()
  const state = useNotifications()
  const markRead = useMarkRead()
  const markAllRead = useMarkAllRead()
  const refresh = useRefreshNotifications()
  const toast = useToast()
  const target = useHitTarget('page')

  /**
   * Both actions unmount the control that was pressed — the row's button when
   * `is_read` flips, the header's when the count reaches zero — and a browser
   * blurs a removed node to `<body>`. That is not merely a lost place: the
   * Escape handler lives on the PANEL, so once focus is on `<body>` the drawer
   * stops closing on Escape entirely. A keyboard user is stuck in a modal one
   * click in.
   *
   * `useFocusRequest` rather than a fifth hand-rolled copy — its docstring
   * names this exact failure, and jsdom sees neither half of it.
   */
  const requestFocus = useFocusRequest({ title })

  // Read during render rather than mirrored into state: the counts are already
  // in the cache, and an effect copying them would be a second source that is
  // one render behind whatever opened the drawer.
  const items = state.status === 'success' || state.status === 'stale' ? state.data.notifications.length : 0

  /**
   * `emit` beside `setOpen`, never inside the updater. `main.tsx` wraps the app
   * in `<StrictMode>`, which double-invokes updater functions in development —
   * so the first draft counted two opens for every one, in dev only, and its
   * own test passed because Testing Library does not mount under StrictMode.
   * An updater must be pure; the two sibling call sites below already were.
   */
  const openDrawer = useCallback(() => {
    if (open) return
    emit('notifications_opened', { unread_count: unread, item_count: items })
    setOpen(true)
  }, [open, unread, items])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.code !== SHORTCUT.code || !event.altKey) return
      if (event.metaKey || event.ctrlKey || event.shiftKey) return
      if (isTyping(event.target)) return
      event.preventDefault()
      openDrawer()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [openDrawer])

  /**
   * Every path reports its outcome, and the failure path is the one that was
   * missing: `.mutate()` swallows a rejection, so the first draft answered a
   * failed PATCH by leaving the row exactly as it was and saying nothing.
   * CLAUDE.md — the error says whose fault and the recovery path — and the
   * toast queue that shipped one commit earlier exists for precisely this.
   *
   * No success toast for a single row: the row visibly loses its control, and
   * a toast per click in a list of twelve is noise. The focus move is what
   * carries it to a screen reader. Read-all DOES get one, because it can empty
   * the whole drawer and the only other signal is an absence.
   */
  const onRead = (id: Notification['id']) => {
    emit('notification_read', { mode: 'single' })
    requestFocus('title')
    markRead.mutate(id, {
      onError: () =>
        toast.show({
          kind: 'error',
          message: 'That notification could not be marked as read.',
          action: { label: 'Try again', onAct: () => onRead(id) },
        }),
    })
  }

  const onReadAll = () => {
    emit('notification_read', { mode: 'all' })
    requestFocus('title')
    markAllRead.mutate(undefined, {
      onSuccess: () => toast.show({ kind: 'success', message: 'All notifications marked as read.' }),
      onError: () =>
        toast.show({
          kind: 'error',
          message: 'Those notifications could not be marked as read.',
          action: { label: 'Try again', onAct: onReadAll },
        }),
    })
  }

  return (
    <>
      <button
        ref={trigger}
        type="button"
        className="notif__bell hoverable"
        /* The count, not just the word: a 6px dot in `--neg` is colour-only by
           construction, and colour is never the only signal. */
        aria-label={`Notifications, ${unread > 0 ? `${unread} unread` : 'no unread'}`}
        /* `haspopup="dialog"`, not `aria-expanded`. Expanded is for a
           disclosure or a combobox, where the region it controls is a DOM
           descendant of the trigger; this one is portalled to `<body>` and is
           modal, which is the shape the APG names haspopup for. */
        aria-haspopup="dialog"
        /* Inline for the same reason `Button` writes its own: this is a floor a
           caller must not be able to shrink. The design draws the control at
           34px, which is under the 44px coarse target, so the target comes
           from the box around the glyph — `useHitTarget`, never a constant,
           because `HIT_TARGET.page` cannot see the phone breakpoint. */
        style={{ minWidth: target, minHeight: target }}
        onClick={openDrawer}
      >
        <span className="notif__bell-face">
          <Icon glyph={Bell} />
          {unread > 0 && <span aria-hidden="true" className="notif__bell-dot" />}
        </span>
      </button>

      <Overlay
        open={open}
        onClose={() => setOpen(false)}
        label="Notifications"
        placement="right"
        restoreFocusTo={trigger}
      >
        <div className="notif">
          <div className="notif__head">
            {/* `tabIndex={-1}` so it can receive focus without becoming a tab
                stop: it is where focus goes when the pressed control removes
                itself, which keeps the user inside the panel — and the panel
                is where the Escape handler lives. */}
            <span className="notif__title" ref={title} tabIndex={-1}>
              Notifications
            </span>
            <span className="notif__head-actions">
              {/* Absent rather than disabled when there is nothing to mark:
                  CLAUDE.md requires a disabled control to state WHICH reason
                  applies, and a drawer header has nowhere to say it. */}
              {unread > 0 && (
                <Button size="page" variant="outline" onClick={onReadAll}>
                  Mark all as read
                </Button>
              )}
              <Button size="page" variant="outline" aria-label="Close notifications" onClick={() => setOpen(false)}>
                <Icon glyph={X} />
              </Button>
            </span>
          </div>
          <div className="notif__body">
            <Async
              surface="notifications"
              state={state}
              skeleton={
                <div className="notif__skeleton">
                  <Skeleton height={44} />
                  <Skeleton height={44} />
                  <Skeleton height={44} />
                </div>
              }
              empty={{
                // Names the missing thing, and the one action is the only one
                // that can change the answer — there is nothing to create here.
                title: 'No notifications yet',
                action: { label: 'Refresh', onAct: refresh },
              }}
            >
              {(list) => <Body list={list} onRead={onRead} />}
            </Async>
          </div>
        </div>
      </Overlay>
    </>
  )
}
